<script>
import LoaderIcon from '@components/loader-icon.vue'
import FormItemGroup from '@components/form-item-group.vue'
import ModalOptInMobilePrompt from '@components/modal-opt-in-mobile-prompt.vue'
import FormItem from '@components/form-item.vue'
import FormItemPhone from '@components/form-item-phone.vue'
import FormItemBirthDay from '@components/form-item-birth-day.vue'
import FormItemAddMultiple from '@components/form-item-add-multiple.vue'
import PersonnelFormLicenseStates from '@components/personnel/personnel-form-license-states.vue'
import { getMottoRoles, getUserDropdownOptions, putUser } from '@src/api/personnelManagement'
import { mottoOptInMobileMixin } from '@src/mixins/mottoOptInMobile'
import { isMobileMixin } from '@src/mixins/isMobileMixin'
import { mottoRolesMixin } from '@src/mixins/mottoRolesMixin'
import { authComputed } from '@state/helpers'
import { getUserReadOnlyRoles } from '@utils/personnel-management-helpers'
import _ from 'lodash'
import { parseErrorMessage } from '@utils/string-helpers'
import PersonnelFormItemCheckbox from '@components/personnel/personnel-form-item-checkbox.vue'
import Tooltip from '@components/tooltip.vue'

export default {
  components: {
    Tooltip,
    PersonnelFormItemCheckbox,
    LoaderIcon,
    ModalOptInMobilePrompt,
    FormItemGroup,
    FormItem,
    FormItemPhone,
    FormItemBirthDay,
    FormItemAddMultiple,
    PersonnelFormLicenseStates,
  },
  mixins: [mottoOptInMobileMixin, isMobileMixin, mottoRolesMixin],
  props: {
    form: {
      type: String,
      required: true,
    },
    userToEdit: {
      type: [Object, null],
      default: null,
    },
    primaryOfficeLicenses: {
      type: [Array, null],
      default: null,
    },
    customClass: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loading: true,
      error: false,
      submitting: false,
      publishToWeb: [],
      hasOptedInEmail: [],
      hasOptedInSMS: [],
      emailNotificationReferral: [],
      user: {},
      timezoneOptions: [],
      mottoRoles: [],
      originalSecondaryEmail: null,
      originalGroups: [],
      isEditing: false,
      offices: [],
    }
  },
  computed: {
    ...authComputed,
    getTimeZoneOptions() {
      return _.values(this.timezoneOptions)
    },
    getTitle() {
      let form = ''
      if (this.form === 'request_licenses') {
        form = 'Licensing'
      }

      if (this.form === 'overview') {
        form = 'My Info'
      }

      if (this.form === 'marketing') {
        form = 'Social Media'
      }

      if (this.form === 'notifications') {
        form = 'Notifications'
      }

      if (this.form === 'roles') {
        form = 'Update Role'
      }

      return `${this.user?.firstName} ${this.user?.lastName} - ${form}`
    },
    hasUserChanged() {
      return !!this.getChangedFields('pmFormModal').length
    },
    getLocationDetailsPlaceholder() {
      if (this.user.loPhase === 2 && this.user.groupLabels.some((x) => x.includes('MLO Phase 2'))) {
        return `${this.user.firstName} is an experienced loan originator at Motto Mortgage ${this.user.primaryOffice.tradeName}. ${this.user.firstName} works directly with multiple wholesale lenders to provide solid, competitive loan options to clients while delivering exceptional customer service and greater transparency.`
      }
      return 'ORM Location Description'
    },
    availableStates() {
      return this.primaryOfficeLicenses?.filter((x) => !this.user.originalLicenses?.some((y) => y.state === x))
    },
  },
  watch: {
    hasOptedInSMS(newValue) {
      this.setAcceptedMobileOptInData(!!newValue[0], false)
    },
  },
  created() {
    this.loadForm()
  },
  methods: {
    getUserReadOnlyRoles,
    async loadForm() {
      try {
        this.loading = true
        const [
          {
            data: {
              data: { timezone: timezoneData },
            },
          },
          {
            data: { data: mottoRoles },
          },
        ] = await Promise.all([getUserDropdownOptions(), getMottoRoles()])
        this.timezoneOptions = timezoneData
        this.mottoMembershipAccount = mottoRoles.filter((role) => role.label === 'Motto Membership')[0]
        this.mottoRoles = this.mottoMembershipAccount.groups
        this.loading = false
      } catch (err) {
        this.error = true
      }
    },
    clearUserVariable() {
      this.user = {}
      this.hasOptedInEmail = []
      this.emailNotificationReferral = []
      this.originalSecondaryEmail = null
      this.originalGroups = []
    },
    loadUserVariable() {
      this.user = _.cloneDeep(this.userToEdit)
      this.originalSecondaryEmail = this.user.secondaryEmail
      this.originalGroups = this.user.groups
      this.setAcceptedMobileOptInData(this.user.optInToMarketingTexts)
      this.hasOptedInSMS = this.user.optInToMarketingTexts ? ['1'] : []
      this.user.timezoneValue = this.timezoneOptions[this.user.timeZone]
      this.hasOptedInEmail = this.user.optInToMarketingEmails ? ['1'] : []
      this.publishToWeb = this.user.publishToWeb ? ['1'] : []
      this.emailNotificationReferral = this.user.emailNotifications?.referral ? ['1'] : []
      this.hadMobilePhoneInitially = !!this.user.phoneMobile
    },
    async saveChanges() {
      try {
        this.submitting = true
        let userDetails = {
          ...this.user,
          optInToMarketingTexts: this.hasAcceptedMobileOptInData && !!this.user.phoneMobile,
          optInToMarketingEmails: !!this.hasOptedInEmail[0],
          publishToWeb: !!this.publishToWeb[0],
          licenses: _.orderBy(this.user.originalLicenses, 'state'),
          requestedLicenses: _.orderBy(this.user.licenses, 'state'),
          emailNotifications: { referral: this.emailNotificationReferral[0] ? 1 : 0 },
          group: null,
          timeZone: _.findKey(this.timezoneOptions, (x) => x === this.user.timezoneValue),
        }

        let isOnlyPhaseTwoChecked = false

        if (!this.user.isAccessOnly && !this.user.isHQUser) {
          userDetails = {
            ...userDetails,
            group: null,
            groups: this.mottoRoles.filter((group) => {
              if (this.user.groupPersonnelLabels.includes('In-house Processor') && group.label === 'Loan Processor') {
                return true
              }

              if (
                this.user.groupPersonnelLabels.includes('Loan Originator Phase 1') &&
                group.label === 'Loan Originator'
              ) {
                return true
              }

              if (
                this.user.groupPersonnelLabels.includes('Loan Originator Phase 2') &&
                group.label === 'Loan Originator' &&
                this.originalGroups.some((group) => group.label === 'Loan Originator')
              ) {
                return true
              }

              return this.user.groupPersonnelLabels.includes(group.label)
            }),
          }

          isOnlyPhaseTwoChecked =
            _.isEmpty(userDetails.groups) && this.user.groupPersonnelLabels.includes('Loan Originator Phase 2')

          if (isOnlyPhaseTwoChecked) {
            return {
              user: { ...this.user },
              shouldRedirectToPhaseTwoRequest: true,
              isOnlyPhaseTwoChecked: true,
            }
          }
        }

        const isLicensesChanged = !_.isEqual(
          _.orderBy(this.user.originalLicenses, 'state'),
          _.orderBy(this.user.licenses, 'state')
        )

        let user = null

        if (isLicensesChanged) {
          const userFresh = await this.$bvModal
            .msgBoxConfirm(
              `This request will be sent to the Motto Product Support team to review.  Do you want to continue?`,
              {
                modalClass: 'confirmation-modal',
                title: 'Confirm',
                okTitle: 'Continue',
                cancelTitle: 'No',
                centered: true,
              }
            )
            .then(async (confirmation) => {
              if (confirmation) {
                const { data: user } = await putUser({
                  userId: userDetails.id,
                  userDetails,
                })
                return user
              }

              return false
            })

          if (!userFresh) {
            return false
          }

          user = userFresh
        } else {
          const { data: userFresh } = await putUser({
            userId: userDetails.id,
            userDetails,
          })

          user = userFresh
        }

        const shouldRedirectToPhaseTwoRequest =
          this.user.groupPersonnelLabels.includes('Loan Originator Phase 2') && !this.user.isLoPhaseTwo

        if (!shouldRedirectToPhaseTwoRequest) {
          this.$root.$bvToast.toast(
            isLicensesChanged
              ? `State License request has been submitted and is under review by Motto Product Support`
              : 'User updated successfully',
            {
              toaster: 'b-toaster-bottom-center',
              toastClass: 'toast-success',
            }
          )
        }

        if (
          this.originalSecondaryEmail !== user.secondaryEmailTemp &&
          this.user.secondaryEmail === user.secondaryEmailTemp
        ) {
          this.$root.$bvToast.toast(
            'A confirmation has been sent to your personal email. Please verify your email address.',
            {
              toaster: 'b-toaster-bottom-center',
              toastClass: 'toast-primary-blue',
            }
          )
        }

        return {
          user,
          shouldRedirectToPhaseTwoRequest,
          isOnlyPhaseTwoChecked,
        }
      } catch (err) {
        const errorMessage = parseErrorMessage(err)
          .toString()
          .replace('secondary email', 'personal email')
          .replace(
            'The record you are working on has been modified by another user. Please refresh the page to obtain the latest update',
            'There was an issue saving your request.  Please refresh the page and try again'
          )
        this.$bvToast.toast(errorMessage, {
          toaster: 'b-toaster-bottom-center',
          toastClass: 'toast-error',
        })
      } finally {
        this.submitting = false
      }
    },
    handlePmFormCancel() {
      this.$bvModal.hide('modalPmForm')
    },
    getChangedFields(formRef) {
      if (!this.$refs[formRef]) {
        return []
      }
      return Object.keys(this.$refs[formRef].fields)
        .filter((key) => this.$refs[formRef].fields[key].changed)
        .sort()
    },
    async handlePmFormOK(bvOkEvent) {
      bvOkEvent.preventDefault()
      const { user, shouldRedirectToPhaseTwoRequest, isOnlyPhaseTwoChecked } = await this.saveChanges()
      if (!user) {
        return
      }
      this.$emit('update-user', {
        user,
        shouldRedirectToPhaseTwoRequest,
        isOnlyPhaseTwoChecked,
      })
      this.$bvModal.hide('modalPmForm')
    },
    mobilePhoneUpdated() {
      if (!this.hasSmsPromptsFeature) {
        return
      }

      if (
        this.user.phoneMobile &&
        this.user.phoneMobile.length === 12 &&
        this.user.phoneMobile !== this.userToEdit.phoneMobile
      ) {
        this.setAcceptedMobileOptInData(true, true)
        this.openOptInMobilePromptModal()
      }
    },
  },
}
</script>

<template>
  <div>
    <ValidationObserver ref="pmFormModal" v-slot="{ failed: validationFailed }" class="d-contents" immediate>
      <b-modal
        id="modalPmForm"
        :title="getTitle"
        :modal-class="customClass ? `${customClass} form-modal` : `form-modal`"
        cancel-title="Cancel"
        ok-title="Submit"
        :centered="true"
        :ok-disabled="!user.id || validationFailed || submitting || !hasUserChanged"
        @show="loadUserVariable"
        @hidden="clearUserVariable"
        @cancel="handlePmFormCancel"
        @ok="handlePmFormOK">
        <LoaderIcon v-if="!user.id" :error="error" class="m-auto"></LoaderIcon>
        <div v-if="user.id">
          <div v-if="form === 'roles'">
            <div>
              <FormItemGroup>
                <PersonnelFormItemCheckbox
                  v-if="!user.isAccessOnly"
                  label="Office Employee"
                  :value.sync="user.groupPersonnelLabels"
                  :edit-mode="true"
                  immediate-validation
                  :rules="user.isBrokerOwner ? '' : 'required'"
                  :checkbox-sub-labels="roleOptionsSubLabels"
                  multi-select
                  :edit-checkbox-values="roleOptions"
                  :exclusive-values="[
                    ['LOA - Licensed', 'LOA - Unlicensed'],
                    ['Loan Originator Phase 1', 'Loan Originator Phase 2'],
                  ]"
                  :disabled-checkbox-values="getUserReadOnlyRoles({ user })"
                  placeholder="Select...">
                  <template v-slot:label>
                    <p class="button-text bold mb-3">Choose all that apply: </p>
                  </template>
                </PersonnelFormItemCheckbox>
              </FormItemGroup>
            </div>
          </div>
          <div v-if="form === 'overview'">
            <div>
              <FormItemGroup>
                <FormItem
                  label="Job Title"
                  :character-limit-options="{ showOnlyError: true }"
                  rules="max:60"
                  edit-mode
                  :value.sync="user.jobTitle" />
                <div></div>
              </FormItemGroup>
              <FormItemGroup>
                <FormItemPhone
                  phone-label="Work Line"
                  edit-mode
                  :disabled="submitting"
                  :phone.sync="user.phone"
                  :phone-ext.sync="user.phoneExt"
                  rules="required|validPhoneNumber" />
                <div>
                  <FormItemPhone
                    phone-label="Mobile Number"
                    edit-mode
                    :disabled="submitting"
                    :phone.sync="user.phoneMobile"
                    :phone-ext.sync="user.phoneMobileExt"
                    phone-ext-label="Ext. Mobile"
                    :rules="hadMobilePhoneInitially ? 'required|validPhoneNumber' : 'validPhoneNumber'"
                    @blur="mobilePhoneUpdated" />
                  <div class="mt-2 ml-2 d-flex align-items-center">
                    <ValidationProvider :name="'publishToWeb'">
                      <BaseCheckbox
                        v-model="publishToWeb"
                        class="mr-2"
                        :class="[user.phoneMobile?.length === 12 ? '' : 'pe-none']"
                        label="Publish to Web"
                        input-value="1" />
                    </ValidationProvider>
                    <Tooltip :target="'publish-to-web'" :icon-absolute-position="false" placement="top" theme="blue">
                      Checking this box will publish your Mobile number on the web.
                    </Tooltip></div
                  >
                </div>
              </FormItemGroup>
              <FormItemGroup>
                <FormItem
                  label="Time Zone"
                  edit-mode
                  :disabled="submitting"
                  :rules="'required'"
                  :edit-select-values="getTimeZoneOptions"
                  :value.sync="user.timezoneValue" />
                <FormItemBirthDay
                  edit-mode
                  :disabled="submitting"
                  :birth-day.sync="user.birthDay"
                  :birth-month.sync="user.birthMonth" />
              </FormItemGroup>
              <FormItemGroup>
                <FormItem label="Motto Email" :value="user.primaryEmail" />
                <FormItem
                  v-if="!user.secondaryEmailTemp"
                  label="Personal Email"
                  edit-mode
                  :disabled="submitting"
                  :character-limit-options="{ showOnlyError: true }"
                  :value.sync="user.secondaryEmail"
                  rules="required|email|max:75" />
                <FormItem
                  v-if="user.secondaryEmailTemp"
                  label="Personal Email"
                  :value="user.secondaryEmailTemp"
                  :tooltip-text="'A confirmation has been sent to your personal email. Please verify your email address.'" />
              </FormItemGroup>
            </div>
          </div>
        </div>
        <div v-if="form === 'marketing'">
          <div>
            <FormItemGroup>
              <FormItem
                label="Facebook"
                placeholder="facebook.com"
                edit-mode
                :disabled="submitting"
                :value.sync="user.facebook" />
              <FormItem
                label="Twitter"
                placeholder="twitter.com"
                edit-mode
                :disabled="submitting"
                :value.sync="user.twitter" />
              <FormItem
                label="LinkedIn"
                placeholder="linkedin.com"
                edit-mode
                :disabled="submitting"
                :value.sync="user.linkedin" />
            </FormItemGroup>
            <FormItemGroup>
              <FormItem
                label="Instagram"
                placeholder="instagram.com"
                edit-mode
                :disabled="submitting"
                :value.sync="user.instagram" />

              <FormItem
                label="Snapchat"
                placeholder="snapchat.com"
                edit-mode
                :disabled="submitting"
                :value.sync="user.snapchat" />
              <div></div>
            </FormItemGroup>
            <FormItemGroup>
              <FormItem label="Website" copyable placeholder="Website" :value="user.domain" />
              <FormItem
                label="Application URL"
                copyable
                placeholder="Application URL"
                :value.sync="user.mortgageApplicLink" />
              <FormItem label="Google My Business" placeholder="google.com" :value="user.google" />
            </FormItemGroup>
            <FormItemGroup>
              <FormItemAddMultiple
                :input-container-class="$style.keywordsInput"
                :value.sync="user.keywords"
                edit-mode
                :disabled="submitting"
                label="SEO Keywords"
                :is-changed="getChangedFields('pmFormModal').includes('SEO Keywords')"
                placeholder="Add Keyword"></FormItemAddMultiple>
            </FormItemGroup>
            <FormItemGroup>
              <FormItem
                label="ORM Location Description"
                :placeholder="getLocationDetailsPlaceholder"
                tooltip-text="The following will be used to fill in any website listing descriptions on websites such as Google, Bing, Yelp, and many more. If you would like something else please enter your own description below.  Limit is 750 characters."
                edit-mode
                textarea
                :disabled="submitting"
                :value.sync="user.locationDetails"
                rules="max:750" />
            </FormItemGroup>
            <FormItemGroup>
              <FormItem
                label="Short Web Description"
                placeholder="Short Web Description"
                tooltip-text="Add a short description of your business that will appear across the web where a short business description is available with your populated information.  Limit is 100 characters."
                edit-mode
                :disabled="submitting"
                :value.sync="user.shortDescription"
                rules="max:100" />
            </FormItemGroup>
          </div>
        </div>
        <div v-if="form === 'notifications'">
          <div>
            <div>
              <div v-if="hasSmsPromptsFeature" class="mb-4">
                <h2>SMS</h2>
                <div class="ml-3 mt-3">
                  <h4 class="font-weight-bold">MottoMortgage Network Alerts</h4>
                  <p class="p1">
                    Opt in to receive important messages regarding marketing, events, and general information about
                    tools, services, and support of the Motto Mortgage network. Message frequency varies. Text HELP to
                    58476 or call 866-668-8660 for help, STOP to 58476 to end. Message and Data rates may apply. By
                    opting in, you authorize Motto Mortgage to deliver marketing messages using an automatic telephone
                    dialing system. You are not required to opt in as a condition of purchasing any property, goods, or
                    services. By checking the box below, you agree to the
                    <RouterLink
                      :to="{ name: 'mobileTerms' }"
                      class="button-text text-decoration-none color-primary-blue"
                      target="_blank"
                      >MOBILE TERMS OF USE</RouterLink
                    >
                    and
                    <a
                      href="https://www.mottomortgage.com/privacy-policy"
                      target="_blank"
                      class="button-text text-decoration-none color-primary-blue"
                      >Motto Mortgage Privacy Policy</a
                    >
                    and
                    <RouterLink
                      :to="{ name: 'userAgreement' }"
                      target="_blank"
                      class="button-text text-decoration-none color-primary-blue"
                      >USER AGREEMENT</RouterLink
                    >.
                  </p>
                  <div :class="$style.optInMobileNumberContainer" class="d-flex mt-3 align-items-center">
                    <ValidationProvider :name="'optInMobile'">
                      <FormItemPhone
                        :class="$style.notificationPhoneSms"
                        class="mt-2 mb-4"
                        phone-label="Mobile Number"
                        :disabled="!hasOptedInSMS[0]"
                        edit-mode
                        hide-ext
                        :options="{ hideAsterisk: true }"
                        phone-placeholder="Mobile Number"
                        :phone.sync="user.phoneMobile"
                        :rules="hasOptedInSMS[0] ? 'required|validPhoneNumber' : ''" />
                      <BaseCheckbox
                        v-model="hasOptedInSMS"
                        label="I’d like to receive text messages from Motto HQ."
                        input-value="1" />
                    </ValidationProvider>
                  </div>
                </div>
              </div>
              <div class="mb-3">
                <h2>Email</h2>
                <div class="ml-3 mt-3">
                  <h4 class="font-weight-bold">Network Connect</h4>
                  <p class="p1">Opt-in to receive email notifications for Network Connect deal-level referrals.</p>
                  <div :class="$style.optInMobileNumberContainer" class="d-flex mt-3 align-items-center">
                    <ValidationProvider :name="'optInEmail'">
                      <BaseCheckbox
                        v-model="emailNotificationReferral"
                        label="Deal-level referral email notifications"
                        input-value="1" />
                    </ValidationProvider>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="form === 'request_licenses'">
          <div>
            <div>
              <FormItemGroup>
                <PersonnelFormLicenseStates
                  label="State(s) of Licensure"
                  edit-mode
                  :rules="'required'"
                  :edit-select-values="availableStates"
                  :read-only-values="user.originalLicenses"
                  :disabled="!(primaryOfficeLicenses && primaryOfficeLicenses.length) || submitting"
                  :placeholder="
                    primaryOfficeLicenses && primaryOfficeLicenses.length
                      ? 'Select...'
                      : 'No states are eligible for this user'
                  "
                  :value.sync="user.licenses" />
              </FormItemGroup>
            </div>
          </div>
        </div>
      </b-modal>
    </ValidationObserver>
    <ModalOptInMobilePrompt
      :mobile-number.sync="user.phoneMobile"
      @ok-opt-in="okOptInMobilePromptModal"
      @cancel-opt-in="cancelOptInMobilePromptModal" />
  </div>
</template>

<style lang="scss" module>
@import '@design';

.keywordsInput {
  max-width: 350px;
}

.notificationPhoneSms {
  > div {
    > div {
      display: flex;
      align-items: baseline;

      > div {
        margin-right: 1em;
      }
    }
  }
}
</style>
